import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { Auth0Provider, AppState, CacheLocation } from '@auth0/auth0-react'
import { PersistGate } from 'redux-persist/integration/react'
import { initSentry } from '@babylon/sentry'
import '@babylon/fonts/Visuelt'

import { Tracker, Callback } from '@/components'
import { getStore, getPersistor, getState } from '@/config/redux'

import config from './config'
import AppWrapper from './AppWrapper'
import { selectLocalisation } from './redux/productConfig/selectors'
import { selectAppName } from './redux/partnerConfig/selectors'
import { selectEnvironment } from './redux/signup/selectors'
import { initialiseTealium } from './tracking'
import { auth0RedirectUri } from '@/constants'

const { SENTRY_URL, VERSION, AUTH0_CLIENT_ID, AUTH0_DOMAIN_URL } = config

if (SENTRY_URL) {
  initSentry({
    dsn: SENTRY_URL,
    release: VERSION,
  })
}

const store = getStore()
const state = getState()

const { region } = selectLocalisation(state)
const environment = selectEnvironment(state)
const appName = selectAppName(state)

const onRedirectCallback = (appState?: AppState) => {
  setTimeout(() => {
    if (appState?.redirectUrl) {
      window.location.href = appState.redirectUrl
    }
  })
}

const auth0ProviderConfig = {
  domain: AUTH0_DOMAIN_URL,
  clientId: AUTH0_CLIENT_ID,
  redirectUri: auth0RedirectUri,
  advancedOptions: {
    defaultScope: 'openid',
  },
  onRedirectCallback,
  xAppName: appName,
  useRefreshTokens: true,
  cacheLocation: 'localstorage' as CacheLocation,
}

initialiseTealium(environment, region)

const Root = () => (
  <Provider store={store}>
    <PersistGate persistor={getPersistor()}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Auth0Provider {...auth0ProviderConfig}>
        <Router>
          <Route path="/callback">
            <Callback />
          </Route>
          <Route path="/get-started">
            <Tracker store={store} />
            <AppWrapper />
          </Route>
        </Router>
      </Auth0Provider>
    </PersistGate>
  </Provider>
)

const rootElement = document.getElementById('root')
ReactDOM.render(<Root />, rootElement)

if (window.Cypress) {
  window.store = store
}
